import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { BehaviorSubject, combineLatest, Observable, Subject } from 'rxjs';
import { filter, map, take } from 'rxjs/operators';
import { Frac, FracStageMesh, FracStorage, GhostSandPartial } from '~lmo/models/frac.model';
import { Mesh, sortByPreferredOrder } from '~lmo/models/mesh.model';
import { LmoFracsService } from '~lmo/services';
import { LmoFracModalService } from '~lmo/services/lmo-frac-modal.service';
import { trackById } from '~utilities/trackById';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'sa-lmo-reset-current-stock',
  styleUrls: ['./lmo-reset-current-stock.component.scss'],
  templateUrl: './lmo-reset-current-stock.component.html',
})
export class LmoResetCurrentStockComponent implements OnInit, OnDestroy {
  private destroy$: Subject<null> = new Subject<null>();
  private frac$: Observable<Frac>;
  public isNetworkActive$$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public meshesInOrder$: Observable<Mesh[]>;
  public storagesInOrder$: Observable<FracStorage[]>;
  public currentStockForm: FormGroup;
  public isReady$$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public trackById = trackById;

  constructor(
    private fracService: LmoFracsService,
    private fb: FormBuilder,
    private modalService: LmoFracModalService,
  ) {
    this.setupMesh = this.setupMesh.bind(this);
  }

  public ngOnInit() {
    this.frac$ = this.fracService.currentFrac$.pipe(filter((frac) => !!frac));

    this.setupMeshAndStorageOrder();

    combineLatest(this.fracService.currentFracCurrentStage$.pipe(filter((currentStage) => !!currentStage)), this.frac$)
      .pipe(take(1))
      .subscribe(([currentStage, frac]) => {
        this.currentStockForm = this.fb.group({});
        currentStage.meshes.forEach((mesh) => this.setupMesh(mesh, frac.fracStorages));
        this.isReady$$.next(true);
      });
  }

  public ngOnDestroy(): void {
    this.destroy$.next(null);
    this.destroy$.unsubscribe();
  }

  public sendGhostSandUpdate(): void {
    this.isNetworkActive$$.next(true);
    if (this.currentStockForm.invalid) {
      this.isNetworkActive$$.next(false);
      return;
    }

    const dirtyValues: { [key: string]: number } = {};
    Object.keys(this.currentStockForm.controls).forEach((key) => {
      if (this.currentStockForm.get(key).dirty) {
        dirtyValues[key] = this.currentStockForm.get(key).value;
      }
    });

    const ghostSandsPartial: GhostSandPartial[] = Object.keys(dirtyValues).map((key) => ({
      currentStock: dirtyValues[key],
      storageId: +key,
    }));

    this.fracService.updateGhostSands$(ghostSandsPartial).subscribe((success) => {
      if (success) {
        this.modalService.closeHomeModal();
      }
      this.isNetworkActive$$.next(false);
    });
  }

  private setupMeshAndStorageOrder() {
    this.meshesInOrder$ = this.frac$.pipe(
      map((frac) => frac.fracStorages.map((storage) => storage.mesh)),
      map((meshes) => sortByPreferredOrder(meshes)),
    );
    this.storagesInOrder$ = combineLatest(this.meshesInOrder$, this.frac$).pipe(
      map(([meshes, frac]) => {
        // This is O(n^2) but there are usually only 2 - 5 meshes per frac so I don't care.
        const storages: FracStorage[] = meshes.map((mesh) =>
          frac.fracStorages.find((storage) => storage.mesh.id === mesh.id),
        );
        return storages;
      }),
    );
  }

  private setupMesh(mesh: FracStageMesh, fracStorages: FracStorage[]) {
    const matchingStorage = fracStorages.find((storage) => storage.mesh.id === mesh.id);
    const matchingStorageId = `${matchingStorage.id}`;
    this.currentStockForm.addControl(
      matchingStorageId,
      this.fb.control(matchingStorage.currentStock || 0, [Validators.required, Validators.min(0)]),
    );
  }
}
